import {
  PhotoIcon as OutlinePhotoIcon,
  PencilIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  PhotoIcon as SolidPhotoIcon,
} from '@heroicons/react/20/solid';
import { debounce } from 'lodash';
import React, {
  ChangeEventHandler,
  SelectHTMLAttributes,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { LoadingIcon } from '@/components/icons';
import useAuth from '@/data/hook/useAuth';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { toolsQueryKeys } from '@/data/services/querykeys';
import { ListToolsFilters } from '@/data/services/toolServices';
import { isStudent } from '@/functions/auth';
import { ApiError } from '@/models/Errors';
import { SimpleTag } from '@/models/Tag';
import Tool from '@/models/Tool';
import { UserTypeEnum } from '@/models/User';
import IconButton from '../../buttons/IconButton';
import Text from '../../dataDisplay/Text';
import { Tooltip } from '../../dataDisplay/Tooltip';
import { BaseInputProps } from '../BaseInput';
import TextInput from '../TextInput';
import TagInput from './TagInput';

export type TagType = 'tool' | 'none';

export type SelectMainTag = {
  tagType: TagType;
  tag: SimpleTag;
};

export type TagInputProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'className'
> &
  BaseInputProps & {
    tagType: TagType;
    onSelectTag<SimpleTag>(tagType: TagType, tag: SimpleTag): void;
    selectedTags?: SimpleTag[];
    openForm?(tagType: TagType, tag: SimpleTag): void;
    onSelectMainTag?({ tag, tagType }: SelectMainTag): void;
  };

export default function ToolInput(props: TagInputProps) {
  const {
    tagType,
    onSelectTag,
    selectedTags,
    openForm,
    onSelectMainTag,
    ...restInputProps
  } = props;

  const { user } = useAuth();

  const { t } = useTranslation('translation', {
    keyPrefix: 'tagInput',
  });

  const [search, setSearch] = useState<string>();

  const [tagError, setTagError] = useState('');

  const tagFilter: ListToolsFilters = {
    name: search,
  };
  const {
    results: tools,
    isInitialLoading: initialLoadingTools,
    isRefetching: refetchingTools,
    error: toolError,
  } = useInfiniteService({
    ...toolsQueryKeys.list(tagFilter)._ctx.infinity,
    keepPreviousData: true,
    enabled: !isStudent(user?.userType) && tagType === 'tool',
  });

  useEffect(() => {
    let mounted = true;
    if (mounted && toolError) {
      const apiError = new ApiError(toolError as any);
      setTagError(apiError.getErrorMessage());
    }

    return () => {
      mounted = false;
      setTagError('');
    };
  }, [toolError]);

  const defineTagType = (tagType: TagType) => {
    const keys = {
      tool: [tools, selectedTags as Tool[]],
      none: [[], selectedTags as SimpleTag[]],
    };

    return keys[tagType];
  };

  const [tags, selected] = defineTagType(tagType);

  const onChangeFilter: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    if (target.value) {
      setSearch(target.value);
    } else {
      setSearch(undefined);
    }
  };

  const addColors = (tag: SimpleTag | Tool): SimpleTag => {
    function instanceOfTag(obj: object): obj is Tool {
      if (obj === null) return false;
      return 'color' in obj;
    }
    const color = instanceOfTag(tag) ? tag.color ?? '#738CFF' : '#738CFF';

    return { ...tag, color };
  };

  const selectedTagsWithColors = selected?.map(addColors);

  return (
    <TagInput
      label={t(tagType)}
      tags={selectedTagsWithColors}
      onDelete={tag => onSelectTag(tagType, tag)}
      isLoading={initialLoadingTools}
      errorLabelText={tagError}
      addTagComponent={
        <TagList
          selectedTags={selectedTags}
          tags={tags}
          refetchingTags={refetchingTools}
          tagType={tagType}
          onSelectTag={onSelectTag}
          onChangeFilter={onChangeFilter}
          openForm={openForm}
          onSelectMainTag={onSelectMainTag}
        />
      }
      {...restInputProps}
    />
  );
}

interface TagListProps extends TagInputProps {
  tags: SimpleTag[];
  selectedTags?: SimpleTag[];
  refetchingTags: boolean;
  onChangeFilter: React.ChangeEventHandler<HTMLInputElement>;
}

function TagList(props: TagListProps) {
  const {
    tags,
    onSelectTag,
    tagType,
    selectedTags,
    refetchingTags,
    onChangeFilter,
    openForm,
    onSelectMainTag,
  } = props;

  const [data, selectedData = []] = defineTagType(tags, tagType, selectedTags);

  type TagWithSelected = SimpleTag & { isSelected: boolean; isMain: boolean };

  const sortSelectedTags = (a: TagWithSelected, b: TagWithSelected) => {
    if (a.isSelected && !b.isSelected) {
      return -1;
    }

    if (!a.isSelected && b.isSelected) {
      return 1;
    }
    return 0;
  };

  const selectedTagsWithOrder = data
    .map<TagWithSelected>(tag => {
      const tool = selectedData?.find(({ id }) => id === tag.id) as Tool;
      return {
        ...tag,
        isSelected: !!tool,
        isMain: tool?.isMain ?? false,
      };
    })
    .sort(sortSelectedTags);

  const debouncedSearchChange = debounce(onChangeFilter, 500);

  const findMain = selectedTagsWithOrder?.find(({ isMain }) => isMain);

  return (
    <div className={mainClassNameList}>
      <TextInput
        fontWeight="font-400"
        icon={<MagnifyingGlassIcon />}
        className={{
          base: `z-50 flex w-full ${refetchingTags ? 'disabled' : ''}`,
        }}
        testId="tagInput"
        autoComplete="off"
        onChange={e => debouncedSearchChange(e)}
      />

      <ConditionalRenderer condition={refetchingTags}>
        <div
          className="flex flex-col items-center relative w-72 z-30 justify-center"
          data-testid="refetchingTags"
        >
          <LoadingIcon className="w-10 text-primary-content" />
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={!refetchingTags && !!data.length}>
        <ul
          data-testid="tagUnorderedList"
          className="flex flex-col w-full gap-2 overflow-y-scroll scrollbar-thumb-rounded-full 
          scrollbar-thin scrollbar-thumb-primary-content scrollbar-track-transparent h-36 overflow-visible"
        >
          {selectedTagsWithOrder?.map((tag, index) => (
            <TagItem
              index={index}
              key={tag.id}
              tag={tag}
              onSelectTag={onSelectTag}
              tagType={tagType}
              openForm={openForm}
              onSelectMainTag={onSelectMainTag}
              selected={tag.isSelected}
              isMain={
                tag?.isMain || (!findMain && index === 0 && tag.isSelected)
              }
            />
          ))}
        </ul>
      </ConditionalRenderer>

      <ConditionalRenderer condition={!data.length && !refetchingTags}>
        <div
          className="flex flex-col items-center relative w-72 z-30"
          data-testid="noTags"
        >
          <Text text="༼ つ ◕_◕ ༽つ" className="pt-2 text-primary" />
        </div>
      </ConditionalRenderer>
    </div>
  );
}

interface TagItemProps<ITag> extends TagInputProps {
  index: number;
  isMain?: boolean;
  tag: ITag;
  selected?: boolean;
  tagType: TagType;
  onSelectTag<ITag>(tagType: TagType, tag: ITag): void;
}

function TagItem<ITag>({
  selected,
  onSelectTag,
  tagType,
  tag,
  openForm,
  onSelectMainTag,
  isMain,
}: TagItemProps<ITag>) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'tagInput',
  });

  const keys = {
    tool: tag as Tool,
    none: tag as any,
  };

  const item = keys[tagType];

  const testId = selected ? `selectedTag${item.id}` : `tagItem${item.id}`;

  const MainTagImageIcon = () => {
    if (isMain) return <SolidPhotoIcon className="w-4" />;
    return <OutlinePhotoIcon className="w-4" />;
  };

  return (
    <li
      data-testid={testId}
      className={`flex gap-2 items-center text-center transition ease-in-out duration-150
                  text-14 px-2 p-1 rounded-lg justify-between ${
                    selected ? 'text-primary' : ''
                  } ${
        isMain
          ? 'bg-primary/40 border-primary border-2'
          : 'border-primary/40 hover:text-primary hover:bg-primary-content border'
      }`}
    >
      <span
        onClick={() => onSelectTag(tagType, item)}
        className="flex gap-4 w-full cursor-pointer"
      >
        {item.name}
      </span>
      <span className="flex gap-4 w-fit">
        <ConditionalRenderer condition={selected}>
          <span className="flex gap-2 items-center">
            <ConditionalRenderer condition={isMain}>
              <Text text={t('banner').toLowerCase()} />
            </ConditionalRenderer>

            <ConditionalRenderer condition={tagType === 'tool'}>
              <Tooltip text={t('mainToolBanner')}>
                <IconButton
                  type="button"
                  icon={<MainTagImageIcon />}
                  onClick={() =>
                    onSelectMainTag && onSelectMainTag({ tagType, tag: item })
                  }
                />
              </Tooltip>
            </ConditionalRenderer>
            <CheckCircleIcon className="w-4" />
          </span>
        </ConditionalRenderer>

        <ComponentGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
          <Tooltip text={t('editTag')}>
            <IconButton
              type="button"
              icon={<PencilIcon className="w-4" />}
              onClick={() => openForm && openForm(tagType, item)}
            />
          </Tooltip>
        </ComponentGuard>
      </span>
    </li>
  );
}

function defineTagType<ITag>(
  tags: ITag[],
  tagType: TagType,
  selectedTags?: ITag[],
) {
  const keys = {
    tool: [tags as Tool[], selectedTags as Tool[]],
    none: [[], selectedTags as ITag[]],
  };

  return keys[tagType];
}

const mainClassNameList =
  'flex flex-col items-center relative w-full z-30 p-4 bg-base-100 rounded-md shadow-default border border-primary-content h-auto max-h-64 gap-2 overflow-visible z-30';
